'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

const PROMPT_PARAM = 'prompt';
const UTM_PREFIX = 'utm_';
// Email links cap prompts well below this; the guard is against hand-crafted URLs.
const MAX_PROMPT_LENGTH = 1000;

export interface DaniDeepLinkPrompt {
    prompt: string;
    utmSource: string;
    utmCampaign: string;
    utmContent: string;
}

/**
 * Captures a `?prompt=` deep link (plus its utm_* params) on the /dani landing
 * page — the arrival point of insight-engine email CTAs — then strips those
 * params from the URL so refresh/back can never replay the link.
 *
 * The capture happens once, into state, BEFORE the strip: ChatInput re-syncs
 * its textarea whenever `initialMessage` changes, so deriving the prompt live
 * from the URL would blank it the moment the params are removed.
 *
 * `enabled` must be the session-ready signal: for anonymous visitors the
 * params have to survive untouched so the sign-in redirect's callbackUrl
 * round-trips them through the login wall.
 */
export function useDaniDeepLinkPrompt(enabled: boolean): DaniDeepLinkPrompt | null {
    const searchParams = useSearchParams();
    const pathname = usePathname();
    const router = useRouter();

    const [captured] = useState<DaniDeepLinkPrompt | null>(() => {
        const prompt = searchParams?.get(PROMPT_PARAM)?.trim();
        if (!prompt) return null;
        return {
            prompt: prompt.slice(0, MAX_PROMPT_LENGTH),
            utmSource: searchParams?.get('utm_source') ?? '',
            utmCampaign: searchParams?.get('utm_campaign') ?? '',
            utmContent: searchParams?.get('utm_content') ?? '',
        };
    });

    const strippedRef = useRef(false);

    useEffect(() => {
        if (!enabled || !captured || strippedRef.current) return;
        strippedRef.current = true;

        const rest = new URLSearchParams();
        searchParams?.forEach((value, key) => {
            if (key !== PROMPT_PARAM && !key.startsWith(UTM_PREFIX)) rest.set(key, value);
        });
        const query = rest.toString();
        router.replace(query ? `${pathname}?${query}` : pathname, { scroll: false });
    }, [enabled, captured, pathname, router, searchParams]);

    return enabled ? captured : null;
}
